/*
 * Copyright © 2025 AionTrack. All rights reserved.
 */

/**
 * VFX Shot Clips API Client
 *
 * Manages many-to-many relationships between VFX shots and clips.
 * Supports multiple clips per VFX shot (plates, comps, mattes, etc.)
 */

import type { VfxShotClip, VfxShotClipRole } from '@/types/database'
import { supabase } from '../supabase'

const API_BASE = import.meta.env.VITE_BACKEND_URL || 'http://localhost:8000'

/**
 * Get auth token from Supabase
 */
async function getAuthToken(): Promise<string> {
  const {
    data: { session },
  } = await supabase.auth.getSession()

  if (!session?.access_token) {
    throw new Error('No authentication session found')
  }

  return session.access_token
}

/**
 * Create a new VFX shot to clip relationship
 */
export async function createVfxShotClipLink(data: {
  vfx_shot_id: string
  clip_id: string
  relationship_type?: VfxShotClipRole
  clip_frame_start?: number
  clip_frame_end?: number
  shot_frame_start?: number
  shot_frame_end?: number
  track_number?: number
  track_name?: string
  notes?: string
  custom_metadata?: Record<string, any>
}): Promise<VfxShotClip> {
  const token = await getAuthToken()

  const response = await fetch(`${API_BASE}/api/vfx-shot-clips`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to create VFX shot clip link' }))
    throw new Error(error.detail || 'Failed to create VFX shot clip link')
  }

  return response.json()
}

/**
 * List VFX shot clip relationships with optional filters
 */
export async function listVfxShotClipLinks(filters?: {
  vfx_shot_id?: string
  clip_id?: string
  relationship_type?: VfxShotClipRole
}): Promise<VfxShotClip[]> {
  const token = await getAuthToken()

  const params = new URLSearchParams()
  if (filters?.vfx_shot_id) params.append('vfx_shot_id', filters.vfx_shot_id)
  if (filters?.clip_id) params.append('clip_id', filters.clip_id)
  if (filters?.relationship_type) params.append('relationship_type', filters.relationship_type)

  const url = `${API_BASE}/api/vfx-shot-clips${params.toString() ? `?${params.toString()}` : ''}`

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to fetch VFX shot clip links' }))
    throw new Error(error.detail || 'Failed to fetch VFX shot clip links')
  }

  return response.json()
}

/**
 * Get a specific VFX shot clip relationship by ID
 */
export async function getVfxShotClipLink(linkId: string): Promise<VfxShotClip> {
  const token = await getAuthToken()

  const response = await fetch(`${API_BASE}/api/vfx-shot-clips/${linkId}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to fetch VFX shot clip link' }))
    throw new Error(error.detail || 'Failed to fetch VFX shot clip link')
  }

  return response.json()
}

/**
 * Update a VFX shot clip relationship
 */
export async function updateVfxShotClipLink(
  linkId: string,
  data: Partial<{
    relationship_type: VfxShotClipRole
    clip_frame_start: number
    clip_frame_end: number
    shot_frame_start: number
    shot_frame_end: number
    track_number: number
    track_name: string
    notes: string
    custom_metadata: Record<string, any>
  }>
): Promise<VfxShotClip> {
  const token = await getAuthToken()

  const response = await fetch(`${API_BASE}/api/vfx-shot-clips/${linkId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to update VFX shot clip link' }))
    throw new Error(error.detail || 'Failed to update VFX shot clip link')
  }

  return response.json()
}

/**
 * Delete a VFX shot clip relationship
 */
export async function deleteVfxShotClipLink(linkId: string): Promise<void> {
  const token = await getAuthToken()

  const response = await fetch(`${API_BASE}/api/vfx-shot-clips/${linkId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to delete VFX shot clip link' }))
    throw new Error(error.detail || 'Failed to delete VFX shot clip link')
  }
}

/**
 * Convenience function: Link a clip to a VFX shot
 * Simpler alternative for basic linking without specifying frame ranges, etc.
 */
export async function linkClipToVfxShot(
  vfxShotId: string,
  clipId: string,
  relationshipType: VfxShotClipRole = 'source'
): Promise<VfxShotClip> {
  const token = await getAuthToken()
  const params = new URLSearchParams({ relationship_type: relationshipType })

  const response = await fetch(
    `${API_BASE}/api/vfx-shots/${vfxShotId}/clips/${clipId}/link?${params.toString()}`,
    {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    }
  )

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to link clip to VFX shot' }))
    throw new Error(error.detail || 'Failed to link clip to VFX shot')
  }

  return response.json()
}

/**
 * Convenience function: Unlink a clip from a VFX shot
 * If relationshipType is specified, only that specific relationship is removed.
 * If not specified, ALL relationships between the VFX shot and clip are removed.
 */
export async function unlinkClipFromVfxShot(
  vfxShotId: string,
  clipId: string,
  relationshipType?: VfxShotClipRole
): Promise<void> {
  const token = await getAuthToken()
  const params = new URLSearchParams()
  if (relationshipType) params.append('relationship_type', relationshipType)

  const response = await fetch(
    `${API_BASE}/api/vfx-shots/${vfxShotId}/clips/${clipId}/unlink${params.toString() ? `?${params.toString()}` : ''}`,
    {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    }
  )

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to unlink clip from VFX shot' }))
    throw new Error(error.detail || 'Failed to unlink clip from VFX shot')
  }
}

/**
 * Get all clips linked to a VFX shot
 */
export async function getVfxShotClips(vfxShotId: string): Promise<VfxShotClip[]> {
  return listVfxShotClipLinks({ vfx_shot_id: vfxShotId })
}

/**
 * Get all VFX shots linked to a clip
 */
export async function getClipVfxShots(clipId: string): Promise<VfxShotClip[]> {
  return listVfxShotClipLinks({ clip_id: clipId })
}
